import { Plus } from 'lucide-react';
import type { RefObject } from 'react';
import { LiksSearch } from '../LiksSearch/LiksSearch';
import { LiksTooltip } from '../LiksTooltip/LiksTooltip';
import type { LiksChatComposerKnowledgeBaseOption } from './LiksChatComposer.types';
import './LiksChatComposerKnowledgeMenu.css';

export type LiksChatComposerKnowledgeMenuProps = {
  menuRef: RefObject<HTMLDivElement | null>;
  tooltip: string;
  menuLabel: string | undefined;
  searchPlaceholder: string | undefined;
  searchLabel: string | undefined;
  searchClearLabel: string;
  emptyLabel: string | undefined;
  loadingLabel: string | undefined;
  addLabel: string;
  options: LiksChatComposerKnowledgeBaseOption[];
  linkedCount: number;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  isOpen: boolean;
  isLocked: boolean;
  isLoading: boolean;
  isControlsDisabled: boolean;
  onToggleOpen: () => void;
  onToggleKnowledgeBase: (knowledgeBaseId: string) => void;
};

export function LiksChatComposerKnowledgeMenu({
  menuRef,
  tooltip,
  menuLabel,
  searchPlaceholder,
  searchLabel,
  searchClearLabel,
  emptyLabel,
  loadingLabel,
  addLabel,
  options,
  linkedCount,
  searchQuery,
  onSearchQueryChange,
  isOpen,
  isLocked,
  isLoading,
  isControlsDisabled,
  onToggleOpen,
  onToggleKnowledgeBase,
}: LiksChatComposerKnowledgeMenuProps) {
  return (
    <div className="liks-chat-composer__menu-wrap" ref={menuRef}>
      <LiksTooltip content={tooltip} placement="top-start">
        <button
          type="button"
          className="liks-chat-composer__attach"
          aria-label={tooltip}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          onClick={onToggleOpen}
          disabled={isControlsDisabled || isLocked}
        >
          <Plus
            className="liks-chat-composer__attach-icon"
            strokeWidth={2}
            aria-hidden
          />
          {linkedCount > 0 && (
            <span
              className="liks-chat-composer__linked-count"
              aria-label={`${linkedCount}`}
            >
              {linkedCount}
            </span>
          )}
        </button>
      </LiksTooltip>
      {isOpen && !isLocked && (
        <div
          className="liks-chat-composer__knowledge-menu"
          role="dialog"
          aria-label={menuLabel}
        >
          <LiksSearch
            value={searchQuery}
            onChange={onSearchQueryChange}
            placeholder={searchPlaceholder}
            clearLabel={searchClearLabel}
            aria-label={searchLabel ?? addLabel}
            variant="on-light"
          />
          <div className="liks-chat-composer__knowledge-list liks-scrollbar">
            {isLoading ? (
              <p className="liks-chat-composer__knowledge-empty">
                {loadingLabel ?? emptyLabel}
              </p>
            ) : options.length === 0 ? (
              <p className="liks-chat-composer__knowledge-empty">
                {emptyLabel}
              </p>
            ) : (
              options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="liks-chat-composer__knowledge-item"
                  onClick={() => onToggleKnowledgeBase(option.id)}
                  disabled={isLocked}
                >
                  <span
                    className={[
                      'liks-chat-composer__knowledge-checkbox',
                      option.isLinked &&
                        'liks-chat-composer__knowledge-checkbox--checked',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-hidden
                  >
                    {option.isLinked ? '✓' : ''}
                  </span>
                  <span className="liks-chat-composer__knowledge-name">
                    {option.name}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
